// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Pure, distance-preserving tree re-rooting — no DOM, no render imports.
//
// Operates on the node graph produced by buildGraph (nodes carry
// parent/children/branchLength/isLeaf/id/name) and emits BRAND-NEW raw nodes
// ({ name?, branchLength, children? }) so the result feeds straight back through
// buildGraph's object-source path. It never recycles annotated graph nodes —
// traverseTree mutates whatever it is handed.

// Emit a fresh subtree rooted at `node`, excluding the `cameFrom` neighbour
// (which is the new parent direction). `incomingEdgeLength` is the length of the
// edge from the new parent down to this fresh node.
//
// Edge-length rule: a downward neighbour (an original child) keeps its own
// branchLength; an upward neighbour (the original parent, now flipped to a
// child) takes THIS node's branchLength — the length that used to live on the
// edge between them.
//
// Knuckle suppression: a non-leaf left with exactly one oriented child is a
// degree-2 pass-through (the old binary root after a flip). It is spliced out,
// its two incident edge lengths summed.
function buildFresh(node, cameFrom, incomingEdgeLength) {
  const specs = [];
  if (node.children) {
    for (const child of node.children) {
      if (child !== cameFrom) {
        specs.push({ neighbour: child, edge: child.branchLength });
      }
    }
  }
  if (node.parent && node.parent !== cameFrom) {
    specs.push({ neighbour: node.parent, edge: node.branchLength });
  }

  if (specs.length === 1 && !node.isLeaf) {
    const only = specs[0];
    return buildFresh(only.neighbour, node, incomingEdgeLength + only.edge);
  }

  const fresh = { branchLength: incomingEdgeLength };
  if (node.isLeaf) {
    fresh.name = node.id; // leaf identity survives the re-parse via its id
  } else if (node.name != null) {
    fresh.name = node.name; // preserve clade labels on internal nodes
  }
  if (specs.length > 0) {
    fresh.children = specs.map((s) => buildFresh(s.neighbour, node, s.edge));
  }
  return fresh;
}

/**
 * Re-root the tree onto the edge above `nodeId`, splitting that edge into `d`
 * (below the new root, toward the node) and `L - d` (above, toward the rest of
 * the tree). Distance-preserving. Returns a fresh raw root node.
 *
 * @param {object} graph - buildGraph output (uses graph.nodeById).
 * @param {string} nodeId
 * @param {number} [d] - offset from `node` along its branch; defaults to L / 2.
 * @returns {{ children: object[] }} a fresh raw tree
 */
export function rerootOnEdge(graph, nodeId, d) {
  const node = graph.nodeById[nodeId];
  if (!node) throw new Error(`rerootOnEdge: unknown node id "${nodeId}"`);
  if (!node.parent) throw new Error("rerootOnEdge: cannot re-root at the root");

  const L = node.branchLength;
  const offset = d == null ? L / 2 : d;

  const side1 = buildFresh(node, node.parent, offset); // node's own subtree
  const side2 = buildFresh(node.parent, node, L - offset); // everything else
  return { children: [side1, side2] };
}

/**
 * Find the midpoint of the tree's longest leaf-to-leaf path (its diameter) and
 * express it as an edge + offset for rerootOnEdge.
 *
 * @param {object} graph - buildGraph output.
 * @returns {{ nodeId: string, d: number } | null} null when < 2 leaves.
 */
export function findMidpoint(graph) {
  const root = graph.root;
  if (!root || root.totalLeaves < 2) return null;

  // Single-source farthest-node walk over the tree treated as undirected.
  function farthest(source) {
    const dist = new Map([[source, 0]]);
    const cameFrom = new Map();
    const stack = [source];
    let far = source;
    let farDist = 0;
    while (stack.length) {
      const cur = stack.pop();
      const dCur = dist.get(cur);
      if (dCur > farDist) {
        farDist = dCur;
        far = cur;
      }
      const neighbours = [];
      if (cur.children) {
        for (const c of cur.children) neighbours.push([c, c.branchLength]);
      }
      if (cur.parent) neighbours.push([cur.parent, cur.branchLength]);
      for (const [nb, edge] of neighbours) {
        if (!dist.has(nb)) {
          dist.set(nb, dCur + edge);
          cameFrom.set(nb, cur);
          stack.push(nb);
        }
      }
    }
    return { far, farDist, cameFrom };
  }

  const u = farthest(root).far; // one diameter endpoint
  const second = farthest(u); // the other endpoint + the back-pointers from u
  const v = second.far;
  const diameter = second.farDist;
  const cameFrom = second.cameFrom;

  if (diameter === 0) return { nodeId: v.id, d: 0 }; // cladogram / all-zero lengths

  const half = diameter / 2;

  // Walk v -> u via cameFrom, accumulating distance from v, until we cross half.
  let prev = v;
  let cumBefore = 0;
  for (let cur = cameFrom.get(v); cur; cur = cameFrom.get(cur)) {
    const childIsPrev = prev.parent === cur;
    const edge = childIsPrev ? prev.branchLength : cur.branchLength;
    if (cumBefore + edge >= half) {
      const offsetFromPrev = half - cumBefore;
      if (childIsPrev) {
        // prev is the child; d is measured from the child (prev) upward.
        return { nodeId: prev.id, d: offsetFromPrev };
      }
      // cur is the child; convert prev-relative offset to a child-relative d.
      return { nodeId: cur.id, d: cur.branchLength - offsetFromPrev };
    }
    cumBefore += edge;
    prev = cur;
  }

  return { nodeId: u.id, d: 0 }; // numerical fallback
}
